import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { IPMAStation } from "@/types/ipma-station/ipma-station.types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { IPMAStationForm } from "./ipma-stations/IPMAStationForm";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { useIPMAStationTable } from "@/hooks/ipma-stations/useIPMAStationTable";
import { useIPMAStationColumns } from "./ipma-stations/columns";
import { useConfirmationDialog } from "@/hooks/ipma-stations/useConfirmationDialog";
import { useData } from "@/hooks/useData";

export const IPMAStations = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedStation, setSelectedStation] = useState<IPMAStation | null>(null);

  // Get basins for the dropdown
  const { data: basins = [] } = useData<{ id: string; name: string }>({
    collectionName: "basins",
  });

  const {
    filteredStations,
    loading,
    refetch,
    searchTerm,
    setSearchTerm,
    showArchived,
    setShowArchived,
    basinFilters,
    setBasinFilters,
    clearFilters,
    basinOptions,
    infoSourceMap,
  } = useIPMAStationTable({ basins });

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  } = useConfirmationDialog(refetch);

  const handleAddStation = () => {
    setSelectedStation(null);
    setFormOpen(true);
  };

  const handleEditStation = (station: IPMAStation) => {
    setSelectedStation(station);
    setFormOpen(true);
  };

  const handleArchiveAction = (station: IPMAStation) => {
    openDialog({
      type: station.isArchived ? "restore" : "archive",
      stationId: station.id,
      stationName: station.name,
    });
  };

  const columns = useIPMAStationColumns(t, handleEditStation, handleArchiveAction, infoSourceMap, basins);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("ipmaStations.filters.basin"),
      options: basinOptions,
      selectedValues: basinFilters,
      onChange: setBasinFilters,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("ipmaStations.title")}</h2>
        {!isAssimagraUser && (
          <Button
            onClick={handleAddStation}
            className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("ipmaStations.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("ipmaStations.searchPlaceholder")}
        filters={filters}
        toggle={[
          {
            label: t("ipmaStations.showArchived"),
            checked: showArchived,
            onCheckedChange: setShowArchived,
          },
        ]}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        {filteredStations.length > 0 ? (
          <DataTable columns={columns} data={filteredStations} pageSize={10} className="w-full" minWidth="auto" />
        ) : (
          <div className="flex justify-center items-center p-8 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500">{t("ipmaStations.noData")}</p>
          </div>
        )}
      </div>

      <IPMAStationForm
        basins={basins}
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        stationToEdit={selectedStation}
        onSuccess={refetch}
      />

      {confirmAction && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={closeDialog}
          onConfirm={handleConfirm}
          title={
            confirmAction.type === "archive"
              ? t("ipmaStations.confirmArchive.title")
              : t("ipmaStations.confirmRestore.title")
          }
          description={t(`ipmaStations.confirm${confirmAction.type === "archive" ? "Archive" : "Restore"}.description`)}
          confirmText={
            isConfirmActionLoading
              ? t(`ipmaStations.confirm${confirmAction.type === "archive" ? "Archive" : "Restore"}.actionLoading`)
              : t(`ipmaStations.confirm${confirmAction.type === "archive" ? "Archive" : "Restore"}.action`)
          }
          cancelText={t("ipmaStations.cancel")}
          variant={confirmAction.type === "archive" ? "destructive" : "default"}
          isLoading={isConfirmActionLoading}
        />
      )}
    </div>
  );
};
